import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getCurrentTenantId } from './tenantStore';

// Clé de stockage scopée par tenant : évite qu'une session BPM (localStorage,
// "Se souvenir de moi") ne "fuite" dans un onglet TSLH du même navigateur.
const USER_STORAGE_NAME = getCurrentTenantId() === 'tslh' ? 'user-storage-tslh' : 'user-storage';

// Storage wrapper that chooses between localStorage and sessionStorage
// based on a persisted flag stored in localStorage under 'remember_me'.
// If 'remember_me' === 'true' => use localStorage; otherwise use sessionStorage.
// We always read the flag from localStorage so it survives browser sessions.
const rememberAwareStorage = {
  getItem: (name: string) => {
    try {
      const remember = window.localStorage.getItem('remember_me');
      const useLocal = remember === 'true';
      const store = useLocal ? window.localStorage : window.sessionStorage;

      // If we're choosing localStorage but the data lives in sessionStorage, migrate it.
      if (useLocal) {
        const val = store.getItem(name) || window.sessionStorage.getItem(name);
        if (val && !window.localStorage.getItem(name)) {
          window.localStorage.setItem(name, val);
        }
      }

      return store.getItem(name);
    } catch (e) {
      console.error('rememberAwareStorage.getItem error', e);
      return null;
    }
  },
  setItem: (name: string, value: string) => {
    try {
      const remember = window.localStorage.getItem('remember_me');
      const useLocal = remember === 'true';
      const store = useLocal ? window.localStorage : window.sessionStorage;
      store.setItem(name, value);
    } catch (e) {
      console.error('rememberAwareStorage.setItem error', e);
    }
  },
  removeItem: (name: string) => {
    try {
      window.localStorage.removeItem(name);
      window.sessionStorage.removeItem(name);
    } catch (e) {
      console.error('rememberAwareStorage.removeItem error', e);
    }
  },
};

interface UserState {
  username: string | null;
  user_id: number | null;  // user_id est INTEGER dans la DB
  currentLeagueId: number | null;
  date_of_birth: string | null;  // Date de naissance de l'utilisateur (format ISO: YYYY-MM-DD)
  // impersonation fields
  is_impersonating?: boolean;
  original_user_id?: number | null;
  original_username?: string | null;
  // session BPM : restreint la navigation aux pages essentielles
  is_bpm_session?: boolean;
  // session TSLH : restreint la navigation aux pages TSLH (parallèle à is_bpm_session)
  is_tslh_session?: boolean;
  setUsername: (username: string | null) => void;
  setUserId: (user_id: number | null) => void;
  setCurrentLeagueId: (leagueId: number | null) => void;
  setDateOfBirth: (date_of_birth: string | null) => void;
  setBpmSession: (value: boolean) => void;
  setTslhSession: (value: boolean) => void;
  clearUser: () => void;
  // impersonation methods
  startImpersonation: (username: string, user_id: number) => void;
  stopImpersonation: () => void;
}

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      username: null,
      user_id: null,
      currentLeagueId: null,
      date_of_birth: null,
      is_impersonating: false,
      original_user_id: null,
      original_username: null,
      is_bpm_session: false,
      is_tslh_session: false,
      setUsername: (username) => set({ username }),
      setUserId: (user_id) => set({ user_id }),
      setCurrentLeagueId: (leagueId) => set({ currentLeagueId: leagueId }),
      setDateOfBirth: (date_of_birth) => set({ date_of_birth }),
      setBpmSession: (value) => set({ is_bpm_session: value }),
      setTslhSession: (value) => set({ is_tslh_session: value }),
      clearUser: () => set({ username: null, user_id: null, currentLeagueId: null, date_of_birth: null, is_impersonating: false, original_user_id: null, original_username: null, is_bpm_session: false, is_tslh_session: false }),
      startImpersonation: (username, user_id) => {
        const state = get();
        if (!state.is_impersonating) {
          set({
            original_user_id: state.user_id,
            original_username: state.username,
            username,
            user_id,
            is_impersonating: true,
          });
        }
      },
      stopImpersonation: () => {
        const state = get();
        if (state.is_impersonating) {
          set({
            username: state.original_username ?? state.username,
            user_id: state.original_user_id ?? state.user_id,
            original_user_id: null,
            original_username: null,
            is_impersonating: false,
          });
        }
      },
    }),
    {
      name: USER_STORAGE_NAME, // Nom utilisé pour le stockage persistant, scopé par tenant
      getStorage: () => rememberAwareStorage as any,
    }
  )
);
